import { useCallback, useEffect, useRef, useState } from 'react';
import pixelateCanvas from '@utils/pixelateCanvas';
import {
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Slider,
  Stack,
  Typography,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UndoIcon from '@mui/icons-material/Undo';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { useTranslation } from 'react-i18next';
import { applyManualBlur, type ManualStroke, type MaskResult } from '@utils/faceMask';

interface ManualBlurEditorProps {
  open: boolean;
  /** Object URL of the raw photo being edited. */
  imageSrc: string | null;
  onCancel: () => void;
  onConfirm: (result: MaskResult) => void;
}

const DEFAULT_BRUSH = 32;

/**
 * Full-screen finger-blur editor. The stylist paints over the photo; painted
 * areas are mosaic-pixelated on "Done" and returned as a MaskResult whose
 * File replaces the raw upload.
 */
const ManualBlurEditor = ({ open, imageSrc, onCancel, onConfirm }: ManualBlurEditorProps) => {
  const { t } = useTranslation();
  const baseRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLCanvasElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const [strokes, setStrokes] = useState<ManualStroke[]>([]);
  const [brush, setBrush] = useState(DEFAULT_BRUSH); // display px
  const [applying, setApplying] = useState(false);
  const [imgSize, setImgSize] = useState<{ w: number; h: number } | null>(null);
  const drawingRef = useRef(false);
  // Pixelated copy of the base image at display size — pointer strokes
  // "reveal" this in real time so the blur is visible while the finger moves.
  const pixelatedRef = useRef<HTMLCanvasElement | null>(null);

  // Reset paint state whenever the editor opens for a (new) photo.
  useEffect(() => {
    if (open) {
      setStrokes([]);
      setBrush(DEFAULT_BRUSH);
      setApplying(false);
      pixelatedRef.current = null;
    }
  }, [open, imageSrc]);

  // Load the image and fit both canvases into the available viewport area.
  useEffect(() => {
    if (!open || !imageSrc) return;
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      const wrap = wrapRef.current;
      if (!wrap) return;
      const availW = wrap.clientWidth;
      const availH = wrap.clientHeight;
      const scale = Math.min(availW / img.naturalWidth, availH / img.naturalHeight, 1);
      const dispW = Math.max(1, Math.round(img.naturalWidth * scale));
      const dispH = Math.max(1, Math.round(img.naturalHeight * scale));

      const base = baseRef.current;
      const overlay = overlayRef.current;
      if (!base || !overlay) return;
      base.width = dispW;
      base.height = dispH;
      overlay.width = dispW;
      overlay.height = dispH;
      const bctx = base.getContext('2d');
      if (bctx) bctx.drawImage(img, 0, 0, dispW, dispH);

      // Build the pixelated copy once per photo — the live-reveal source.
      const pix = document.createElement('canvas');
      pix.width = dispW;
      pix.height = dispH;
      const pctx = pix.getContext('2d');
      if (pctx) {
        pctx.drawImage(img, 0, 0, dispW, dispH);
        pixelateCanvas(pix);
      }
      pixelatedRef.current = pix;

      setImgSize({ w: img.naturalWidth, h: img.naturalHeight });
    };
    img.src = imageSrc;
    return () => {
      cancelled = true;
    };
  }, [open, imageSrc]);

  // Redraw all strokes onto the overlay (used on undo / brush change).
  // Each painted circle "stamps" the pixelated copy, so the overlay shows real
  // blur — the same mosaic the exported file will get — while painting.
  const redrawOverlay = useCallback(
    (list: ManualStroke[]) => {
      const overlay = overlayRef.current;
      const pix = pixelatedRef.current;
      if (!overlay || !pix) return;
      const ctx = overlay.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, overlay.width, overlay.height);
      for (const s of list) {
        for (const p of s.points) {
          ctx.save();
          ctx.beginPath();
          ctx.arc(p.x * overlay.width, p.y * overlay.height, brush, 0, Math.PI * 2);
          ctx.clip();
          ctx.drawImage(pix, 0, 0);
          ctx.restore();
        }
      }
    },
    [brush],
  );

  useEffect(() => {
    redrawOverlay(strokes);
  }, [strokes, redrawOverlay]);

  const toNorm = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const overlay = overlayRef.current!;
    const rect = overlay.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)),
    };
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (applying) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drawingRef.current = true;
    setStrokes(prev => [...prev, { points: [toNorm(e)] }]);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current || applying) return;
    const pt = toNorm(e);
    // Live reveal: stamp the pixelated copy directly onto the overlay for
    // instant feedback. React state updates only on pointerup (cheaper).
    const overlay = overlayRef.current;
    const pix = pixelatedRef.current;
    if (overlay && pix) {
      const ctx = overlay.getContext('2d');
      if (ctx) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(pt.x * overlay.width, pt.y * overlay.height, brush, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(pix, 0, 0);
        ctx.restore();
      }
    }
    setStrokes(prev => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      const updated = { ...last, points: [...last.points, pt] };
      return [...prev.slice(0, -1), updated];
    });
  };

  const handlePointerUp = () => {
    drawingRef.current = false;
  };

  const handleUndo = () => setStrokes(prev => prev.slice(0, -1));
  const handleReset = () => setStrokes([]);

  const handleDone = async () => {
    if (!imageSrc || !imgSize) return;
    setApplying(true);
    try {
      // Brush radius as a fraction of min(naturalW, naturalH) — the util maps
      // it to export pixels, independent of display size.
      const brushNorm =
        brush / (Math.min(baseRef.current?.width ?? 1, baseRef.current?.height ?? 1) || 1);
      const result = await applyManualBlur(imageSrc, strokes, brushNorm);
      onConfirm(result);
    } catch (err) {
      console.warn('Manual blur failed:', err);
      // Keep the editor open so the stylist can retry or cancel.
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={applying ? undefined : onCancel}
      maxWidth={false}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0,0,0,0.95)',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', p: 1 }}>
        <IconButton aria-label="close" onClick={onCancel} disabled={applying} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
        <Typography variant="body1" sx={{ color: '#fff', ml: 1 }}>
          {t('liveReview.photo.manualBlur')}
        </Typography>
      </Box>

      <Box sx={{ px: 2, pb: 1 }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          {t('liveReview.photo.manualBlurHint')}
        </Typography>
      </Box>

      <Box
        ref={wrapRef}
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box sx={{ position: 'relative', touchAction: 'none' }}>
          <canvas
            ref={baseRef}
            style={{ display: 'block', borderRadius: 4 }}
          />
          <canvas
            ref={overlayRef}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
            style={{
              position: 'absolute',
              inset: 0,
              touchAction: 'none',
              cursor: 'crosshair',
            }}
          />
        </Box>
      </Box>

      <Box sx={{ px: 3, pb: 1 }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }} gutterBottom>
          {t('liveReview.photo.manualBrush')}
        </Typography>
        <Slider
          value={brush}
          min={16}
          max={64}
          step={4}
          disabled={applying}
          onChange={(_e, v) => setBrush(v as number)}
          sx={{ color: '#fff' }}
        />
      </Box>

      <Stack direction="row" spacing={1} sx={{ p: 2, justifyContent: 'center' }}>
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<UndoIcon />}
          onClick={handleUndo}
          disabled={applying || strokes.length === 0}
          sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}
        >
          {t('liveReview.photo.manualUndo')}
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          startIcon={<RestartAltIcon />}
          onClick={handleReset}
          disabled={applying || strokes.length === 0}
          sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}
        >
          {t('liveReview.photo.manualReset')}
        </Button>
        <Button
          variant="contained"
          onClick={handleDone}
          disabled={applying || strokes.length === 0}
          sx={{ minWidth: 96 }}
        >
          {applying ? <CircularProgress size={22} color="inherit" /> : t('liveReview.photo.manualDone')}
        </Button>
        <Button variant="text" color="inherit" onClick={onCancel} disabled={applying} sx={{ color: 'rgba(255,255,255,0.8)' }}>
          {t('liveReview.photo.manualCancel')}
        </Button>
      </Stack>
    </Dialog>
  );
};

export default ManualBlurEditor;
